import { useQuery, useMutation } from "@tanstack/react-query";
import api from "../axios-client";
import { queryClient } from "../query-client";

// ── Queries ──

export const useGetServiceTypes = (deviceId: string) =>
    useQuery({
        queryKey: ["devices", deviceId, "services", "types"],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${deviceId}/services/types`);
            return response.data.data;
        },
        retry: false,
        staleTime: 1000 * 60 * 60,
        enabled: !!deviceId,
    });

export const useGetServiceRecords = (deviceId: string) =>
    useQuery({
        queryKey: ["devices", deviceId, "services"],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${deviceId}/services`);
            return response.data.data;
        },
        retry: false,
        staleTime: 1000 * 60 * 5,
        enabled: !!deviceId,
    });

export const useGetServiceSchedules = (deviceId: string) =>
    useQuery({
        queryKey: ["devices", deviceId, "services", "schedule"],
        queryFn: async () => {
            const response = await api.get(`/api/v1/devices/${deviceId}/services/schedule`);
            return response.data.data;
        },
        retry: false,
        staleTime: 1000 * 60 * 5,
        enabled: !!deviceId,
    });

// ── Service Record Mutations ──

export const useCreateServiceRecord = () =>
    useMutation({
        mutationFn: async ({ deviceId, data }: { deviceId: string; data: any }) => {
            const response = await api.post(`/api/v1/devices/${deviceId}/services`, data);
            return response.data.data;
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services"] });
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services", "schedule"] });
        },
    });

export const useUpdateServiceRecord = () =>
    useMutation({
        mutationFn: async ({ deviceId, recordId, data }: { deviceId: string; recordId: string; data: any }) => {
            const response = await api.put(`/api/v1/devices/${deviceId}/services/${recordId}`, data);
            return response.data.data;
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services"] });
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services", "schedule"] });
        },
    });

export const useDeleteServiceRecord = () =>
    useMutation({
        mutationFn: async ({ deviceId, recordId }: { deviceId: string; recordId: string }) => {
            const response = await api.delete(`/api/v1/devices/${deviceId}/services/${recordId}`);
            return response.data.data;
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services"] });
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services", "schedule"] });
        },
    });

// ── Service Type Mutations ──

export const useCreateServiceType = () =>
    useMutation({
        mutationFn: async ({ deviceId, data }: { deviceId: string; data: any }) => {
            const response = await api.post(`/api/v1/devices/${deviceId}/services/types`, data);
            return response.data.data;
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services", "types"] });
        },
    });

export const useUpdateServiceType = () =>
    useMutation({
        mutationFn: async ({ deviceId, typeId, data }: { deviceId: string; typeId: string; data: any }) => {
            const response = await api.put(`/api/v1/devices/${deviceId}/services/types/${typeId}`, data);
            return response.data.data;
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services", "types"] });
        },
    });

export const useDeleteServiceType = () =>
    useMutation({
        mutationFn: async ({ deviceId, typeId }: { deviceId: string; typeId: string }) => {
            const response = await api.delete(`/api/v1/devices/${deviceId}/services/types/${typeId}`);
            return response.data.data;
        },
        onSuccess: (_, { deviceId }) => {
            queryClient.refetchQueries({ queryKey: ["devices", deviceId, "services", "types"] });
        },
    });
